<template>
  <div class="digital-transformation">
    <UnifiedNavBar />
    
    <!-- 页面头部 -->
    <section class="page-header">
      <div class="container">
        <h1 class="page-title">数字化转型</h1>
        <p class="page-subtitle">助力传统制造业向数字化、智能化转型升级，构建面向未来的智能制造新生态</p>
      </div>
    </section>

    <!-- 转型概述 -->
    <section class="transformation-overview">
      <div class="container">
        <h2 class="section-title">转型概述</h2>
        <p class="section-description">
          数字化转型是制造业发展的必然趋势，通过数字技术与传统制造深度融合，实现生产方式、管理模式和商业模式的全面变革
        </p>
        <div class="overview-content">
          <div class="overview-text">
            <div class="overview-item" v-for="item in overviewItems" :key="item.id">
              <div class="item-icon">{{ item.icon }}</div>
              <div class="item-content">
                <h3 class="item-title">{{ item.title }}</h3>
                <p class="item-desc">{{ item.desc }}</p>
              </div>
            </div>
          </div>
          <div class="overview-image">
            <img src="https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Digital%20transformation%20concept%20with%20factory%20automation%20AI%20cloud%20computing%20data%20analytics%20modern%20industrial%20design%20blue%20gradient&image_size=landscape_4_3" alt="数字化转型概念图">
          </div>
        </div>
      </div>
    </section>

    <!-- 转型路径 -->
    <section class="transformation-path">
      <div class="container">
        <h2 class="section-title">转型路径</h2>
        <p class="section-description">
          基于行业最佳实践，为企业提供分阶段、可落地的数字化转型实施路径
        </p>
        <div class="path-timeline">
          <div class="timeline-item" v-for="(phase, index) in transformationPhases" :key="phase.id">
            <div class="timeline-marker">
              <span class="phase-number">{{ index + 1 }}</span>
            </div>
            <div class="timeline-content">
              <h3 class="phase-title">{{ phase.title }}</h3>
              <p class="phase-desc">{{ phase.desc }}</p>
              <div class="phase-features">
                <div class="feature-item" v-for="feature in phase.features" :key="feature">
                  <span class="feature-icon">✓</span>
                  <span class="feature-text">{{ feature }}</span>
                </div>
              </div>
              <div class="phase-duration">预计周期：{{ phase.duration }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 核心技术 -->
    <section class="core-technologies">
      <div class="container">
        <h2 class="section-title">核心技术</h2>
        <p class="section-description">
          运用前沿数字技术，构建完整的数字化转型技术体系
        </p>
        <div class="technologies-grid">
          <div class="tech-card" v-for="tech in coreTechnologies" :key="tech.id">
            <div class="tech-icon">{{ tech.icon }}</div>
            <h3 class="tech-title">{{ tech.title }}</h3>
            <p class="tech-desc">{{ tech.desc }}</p>
            <div class="tech-applications">
              <span v-for="app in tech.applications" :key="app" class="app-tag">{{ app }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 解决方案 -->
    <section class="solutions">
      <div class="container">
        <h2 class="section-title">解决方案</h2>
        <p class="section-description">
          针对不同行业和企业规模，提供定制化的数字化转型解决方案
        </p>
        <div class="solutions-tabs">
          <div class="tab-buttons">
            <button 
              v-for="solution in solutions" 
              :key="solution.id"
              :class="['tab-button', { active: activeSolution === solution.id }]"
              @click="activeSolution = solution.id"
            >
              {{ solution.title }}
            </button>
          </div>
          <div class="tab-content">
            <div v-for="solution in solutions" :key="solution.id" v-show="activeSolution === solution.id" class="solution-content">
              <div class="solution-info">
                <h3 class="solution-title">{{ solution.title }}</h3>
                <p class="solution-desc">{{ solution.desc }}</p>
                <div class="solution-features">
                  <div class="feature-group" v-for="group in solution.featureGroups" :key="group.title">
                    <h4 class="group-title">{{ group.title }}</h4>
                    <ul class="group-features">
                      <li v-for="feature in group.features" :key="feature">{{ feature }}</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="solution-image">
                <img :src="solution.image" :alt="solution.title">
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 成功案例 -->
    <section class="success-stories">
      <div class="container">
        <h2 class="section-title">成功案例</h2>
        <p class="section-description">
          与众多企业携手完成数字化转型，在各个行业积累了丰富的实施经验
        </p>
        <div class="stories-grid">
          <div class="story-card" v-for="story in successStories" :key="story.id">
            <div class="story-header">
              <div class="company-info">
                <h3 class="company-name">{{ story.company }}</h3>
                <span class="company-industry">{{ story.industry }}</span>
              </div>
              <div class="story-scale">{{ story.scale }}</div>
            </div>
            <p class="story-challenge"><strong>挑战：</strong>{{ story.challenge }}</p>
            <p class="story-solution"><strong>方案：</strong>{{ story.solution }}</p>
            <div class="story-results">
              <h4>转型成果：</h4>
              <div class="results-grid">
                <div class="result-item" v-for="result in story.results" :key="result.metric">
                  <div class="result-value">{{ result.value }}</div>
                  <div class="result-metric">{{ result.metric }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 转型价值 -->
    <section class="transformation-value">
      <div class="container">
        <h2 class="section-title">转型价值</h2>
        <p class="section-description">
          数字化转型为企业带来全方位的价值提升，助力企业在数字经济时代保持竞争优势
        </p>
        <div class="value-grid">
          <div class="value-card" v-for="value in transformationValues" :key="value.id">
            <div class="value-icon">{{ value.icon }}</div>
            <h3 class="value-title">{{ value.title }}</h3>
            <div class="value-metrics">
              <div class="metric-item" v-for="metric in value.metrics" :key="metric.label">
                <span class="metric-value">{{ metric.value }}</span>
                <span class="metric-label">{{ metric.label }}</span>
              </div>
            </div>
            <p class="value-desc">{{ value.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer组件 -->
    <Footer />
  </div>
</template>

<script>
import UnifiedNavBar from '@/components/UnifiedNavBar.vue'
import Footer from '@/components/Footer.vue'

export default {
  name: 'DigitalTransformation',
  components: {
    UnifiedNavBar,
    Footer
  },
  data() {
    return {
      activeSolution: 1,
      overviewItems: [
        {
          id: 1,
          icon: '🎯',
          title: '战略目标',
          desc: '通过数字化技术重塑业务流程，提升运营效率，创造新的商业价值'
        },
        {
          id: 2,
          icon: '🔄',
          title: '转型路径',
          desc: '从数字化基础设施建设到智能化应用，循序渐进实现全面转型'
        },
        {
          id: 3,
          icon: '📈',
          title: '预期收益',
          desc: '显著提升生产效率、降低运营成本、增强市场竞争力'
        }
      ],
      transformationPhases: [
        {
          id: 1,
          title: '数字化基础建设',
          desc: '建立数字化基础设施，实现设备联网和数据采集',
          features: ['工业网络建设', '设备数字化改造', '数据采集系统', '基础平台搭建'],
          duration: '3-6个月'
        },
        {
          id: 2,
          title: '流程数字化',
          desc: '将传统业务流程数字化，建立数字化管理体系',
          features: ['业务流程梳理', '数字化工具部署', '人员培训', '制度建设'],
          duration: '6-12个月'
        },
        {
          id: 3,
          title: '数据驱动优化',
          desc: '基于数据分析优化业务流程，实现精细化管理',
          features: ['数据分析平台', '业务智能应用', '决策支持系统', '持续优化'],
          duration: '12-18个月'
        },
        {
          id: 4,
          title: '智能化升级',
          desc: '引入人工智能技术，实现智能化生产和管理',
          features: ['AI算法应用', '智能决策系统', '自动化升级', '生态协同'],
          duration: '18-24个月'
        }
      ],
      coreTechnologies: [
        {
          id: 1,
          icon: '☁️',
          title: '云计算',
          desc: '提供弹性、可扩展的计算资源，支撑数字化应用的快速部署和运行',
          applications: ['私有云', '混合云', '边缘计算', '容器化']
        },
        {
          id: 2,
          icon: '📊',
          title: '大数据',
          desc: '构建企业数据湖，实现海量数据的存储、处理和分析',
          applications: ['数据仓库', '实时分析', '数据挖掘', '可视化']
        },
        {
          id: 3,
          icon: '🤖',
          title: '人工智能',
          desc: '运用机器学习、深度学习等AI技术，实现智能化决策和自动化',
          applications: ['机器学习', '计算机视觉', '自然语言处理', '预测分析']
        },
        {
          id: 4,
          icon: '🔗',
          title: '物联网',
          desc: '连接各类设备和系统，实现万物互联和数据实时采集',
          applications: ['传感器网络', '设备监控', '远程控制', '边缘智能']
        },
        {
          id: 5,
          icon: '🔒',
          title: '网络安全',
          desc: '构建多层次安全防护体系，保障数字化转型过程中的信息安全',
          applications: ['身份认证', '数据加密', '安全监控', '风险评估']
        },
        {
          id: 6,
          title: '区块链',
          icon: '⛓️',
          desc: '利用区块链技术确保数据可信、可追溯，构建可信的数字化生态',
          applications: ['供应链溯源', '数据确权', '智能合约', '去中心化']
        }
      ],
      solutions: [
        {
          id: 1,
          title: '智能制造',
          desc: '面向制造企业的全流程数字化转型解决方案，从生产计划到质量管控的全面智能化',
          image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Smart%20manufacturing%20solution%20with%20automated%20production%20line%20robots%20quality%20control%20digital%20dashboard%20modern%20factory&image_size=landscape_4_3',
          featureGroups: [
            {
              title: '生产管理',
              features: ['智能排产', '实时监控', '质量追溯', '设备管理']
            },
            {
              title: '供应链协同',
              features: ['供应商管理', '库存优化', '物流跟踪', '需求预测']
            }
          ]
        },
        {
          id: 2,
          title: '智慧园区',
          desc: '构建智慧化的工业园区管理平台，实现园区资源的统一管理和优化配置',
          image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Smart%20industrial%20park%20with%20IoT%20sensors%20energy%20management%20security%20systems%20digital%20twin%20aerial%20view&image_size=landscape_4_3',
          featureGroups: [
            {
              title: '基础设施',
              features: ['能源管理', '环境监测', '安防系统', '交通管理']
            },
            {
              title: '服务平台',
              features: ['企业服务', '政务服务', '金融服务', '物流服务']
            }
          ]
        },
        {
          id: 3,
          title: '数字化工厂',
          desc: '打造全数字化的智能工厂，实现从设计到制造的全流程数字化管理',
          image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Digital%20factory%20with%20holographic%20displays%20automated%20systems%20data%20visualization%20futuristic%20manufacturing&image_size=landscape_4_3',
          featureGroups: [
            {
              title: '数字化设计',
              features: ['CAD/CAM集成', '仿真验证', '工艺优化', '虚拟调试']
            },
            {
              title: '智能制造',
              features: ['柔性生产', '自适应控制', '预测维护', '质量管控']
            }
          ]
        }
      ],
      successStories: [
        {
          id: 1,
          company: '某大型汽车制造企业',
          industry: '汽车制造',
          scale: '年产值500亿+',
          challenge: '传统生产模式效率低下，质量管控困难，供应链协同不足',
          solution: '部署智能制造解决方案，建设数字化工厂，实现全流程智能化管理',
          results: [
            { value: '35%', metric: '生产效率提升' },
            { value: '50%', metric: '质量缺陷减少' },
            { value: '25%', metric: '库存成本降低' },
            { value: '30%', metric: '交付周期缩短' }
          ]
        },
        {
          id: 2,
          company: '某钢铁集团',
          industry: '钢铁冶金',
          scale: '年产钢1000万吨',
          challenge: '能耗高、污染重，生产过程难以精确控制，环保压力大',
          solution: '实施智慧冶金解决方案，建设绿色智能工厂，优化生产工艺',
          results: [
            { value: '20%', metric: '能耗降低' },
            { value: '40%', metric: '排放减少' },
            { value: '15%', metric: '成本节约' },
            { value: '99.2%', metric: '设备可用率' }
          ]
        },
        {
          id: 3,
          company: '某化工园区',
          industry: '石油化工',
          scale: '入驻企业200+',
          challenge: '安全风险高，环保要求严，园区管理复杂，协同效率低',
          solution: '建设智慧园区平台，实现安全环保一体化管理，提升协同效率',
          results: [
            { value: '100%', metric: '安全事故预防' },
            { value: '60%', metric: '应急响应提速' },
            { value: '30%', metric: '管理成本降低' },
            { value: '45%', metric: '服务效率提升' }
          ]
        }
      ],
      transformationValues: [
        {
          id: 1,
          icon: '⚡',
          title: '效率提升',
          metrics: [
            { value: '30-50%', label: '生产效率提升' },
            { value: '40-60%', label: '决策效率提升' }
          ],
          desc: '通过自动化和智能化技术，显著提升生产和管理效率'
        },
        {
          id: 2,
          icon: '💰',
          title: '成本降低',
          metrics: [
            { value: '20-35%', label: '运营成本降低' },
            { value: '25-40%', label: '维护成本降低' }
          ],
          desc: '优化资源配置，减少浪费，实现成本的有效控制'
        },
        {
          id: 3,
          icon: '🎯',
          title: '质量提升',
          metrics: [
            { value: '30-50%', label: '质量缺陷减少' },
            { value: '95%+', label: '客户满意度' }
          ],
          desc: '通过精确控制和智能检测，大幅提升产品质量'
        },
        {
          id: 4,
          icon: '🚀',
          title: '创新能力',
          metrics: [
            { value: '50%+', label: '新产品开发提速' },
            { value: '3倍+', label: '创新项目增长' }
          ],
          desc: '数字化工具和平台为创新提供强大支撑'
        }
      ]
    }
  }
}
</script>

<style scoped>
/* 页面头部样式 */
.page-header {
  background: linear-gradient(135deg, #0052cc 0%, #1e90ff 100%);
  color: white;
  padding: 120px 0 80px;
  text-align: center;
}

.page-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.page-subtitle {
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto;
  opacity: 0.9;
}

/* 通用区域样式 */
.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.section-description {
  font-size: 1.1rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: #666;
  line-height: 1.6;
}

/* 转型概述样式 */
.transformation-overview {
  padding: 80px 0;
  background: #f8f9fa;
}

.overview-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
}

.overview-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 2rem;
}

.item-icon {
  font-size: 2.5rem;
  margin-right: 1rem;
  flex-shrink: 0;
}

.item-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.item-desc {
  color: #666;
  line-height: 1.6;
}

.overview-image img {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 82, 204, 0.15);
}

/* 转型路径样式 */
.transformation-path {
  padding: 80px 0;
}

.path-timeline {
  position: relative;
  max-width: 800px;
  margin: 0 auto;
}

.path-timeline::before {
  content: '';
  position: absolute;
  left: 30px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e9ecef;
}

.timeline-item {
  position: relative;
  margin-bottom: 3rem;
  display: flex;
  align-items: flex-start;
}

.timeline-marker {
  position: relative;
  z-index: 2;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0052cc 0%, #1e90ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 2rem;
  flex-shrink: 0;
}

.phase-number {
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
}

.timeline-content {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 82, 204, 0.1);
  flex: 1;
}

.phase-title {
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.phase-desc {
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.phase-features {
  margin-bottom: 1rem;
}

.feature-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.feature-icon {
  color: #28a745;
  margin-right: 0.5rem;
  font-weight: bold;
}

.feature-text {
  color: #555;
  font-size: 0.9rem;
}

.phase-duration {
  color: #0052cc;
  font-weight: 600;
  font-size: 0.9rem;
}

/* 核心技术样式 */
.core-technologies {
  padding: 80px 0;
  background: #f8f9fa;
}

.technologies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.tech-card {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 82, 204, 0.1);
  transition: all 0.3s ease;
}

.tech-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 82, 204, 0.2);
}

.tech-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.tech-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.tech-desc {
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.tech-applications {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
}

.app-tag {
  background: #e3f2fd;
  color: #0052cc;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

/* 解决方案样式 */
.solutions {
  padding: 80px 0;
}

.solutions-tabs {
  max-width: 1200px;
  margin: 0 auto;
}

.tab-buttons {
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  gap: 1rem;
}

.tab-button {
  padding: 1rem 2rem;
  border: 2px solid #e9ecef;
  background: white;
  color: #666;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}

.tab-button.active {
  border-color: #0052cc;
  background: #0052cc;
  color: white;
}

.tab-button:hover {
  border-color: #0052cc;
  color: #0052cc;
}

.tab-button.active:hover {
  color: white;
}

.solution-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
}

.solution-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.solution-desc {
  color: #666;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.solution-features {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.feature-group {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
}

.group-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #0052cc;
}

.group-features {
  list-style: none;
  padding: 0;
}

.group-features li {
  padding: 0.3rem 0;
  color: #555;
  font-size: 0.9rem;
}

.group-features li:before {
  content: '✓';
  margin-right: 0.5rem;
  color: #28a745;
  font-weight: bold;
}

.solution-image img {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 82, 204, 0.15);
}

/* 成功案例样式 */
.success-stories {
  padding: 80px 0;
  background: #f8f9fa;
}

.stories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
}

.story-card {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 82, 204, 0.1);
  transition: all 0.3s ease;
}

.story-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 82, 204, 0.2);
}

.story-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.company-name {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.3rem;
}

.company-industry {
  background: #0052cc;
  color: white;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
}

.story-scale {
  color: #666;
  font-size: 0.9rem;
  font-weight: 500;
}

.story-challenge,
.story-solution {
  margin-bottom: 1rem;
  line-height: 1.6;
  color: #555;
}

.story-results h4 {
  color: #2c3e50;
  margin-bottom: 1rem;
}

.results-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.result-item {
  text-align: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.result-value {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #0052cc;
}

.result-metric {
  font-size: 0.8rem;
  color: #666;
}

/* 转型价值样式 */
.transformation-value {
  padding: 80px 0;
}

.value-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.value-card {
  background: linear-gradient(135deg, #0052cc 0%, #1e90ff 100%);
  color: white;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  transition: all 0.3s ease;
}

.value-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 82, 204, 0.3);
}

.value-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.value-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.value-metrics {
  margin-bottom: 1rem;
}

.metric-item {
  margin-bottom: 0.8rem;
}

.metric-value {
  display: block;
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.2rem;
}

.metric-label {
  font-size: 0.9rem;
  opacity: 0.9;
}

.value-desc {
  font-size: 0.9rem;
  opacity: 0.9;
  line-height: 1.5;
}



/* 响应式设计 */
@media (max-width: 768px) {
  .page-title {
    font-size: 2rem;
  }
  
  .page-subtitle {
    font-size: 1rem;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .overview-content,
  .solution-content {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  .tab-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .solution-features {
    grid-template-columns: 1fr;
  }
  
  .technologies-grid,
  .stories-grid,
  .value-grid {
    grid-template-columns: 1fr;
  }
  
  .results-grid {
    grid-template-columns: 1fr;
  }
}
</style>